import { generateClient } from 'aws-amplify/api';

import * as subscriptions from '@/amplifyGraphql/subscriptions';

import { usePlannerStore } from '@/store/planner';

import { computeSessionLabel } from '@/utils/helpers';

import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { fromUnixTime } from 'date-fns/fromUnixTime';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';

const timeZone = 'Europe/Paris';

const groupedType = ['TERRAIN', 'POSTE'];
const groupedActivity = ['TENNIS_TABLE'];

const frenchDate = (t, date) => toZonedTime(date ? new Date(date) : new Date(), t || timeZone);

const frenchFormatedDate = (t, date, dateFormat) => formatInTimeZone(date ? new Date(date) : new Date(), t || timeZone, dateFormat);

const unsubscribe = async () => {
  const plannerStore = usePlannerStore();

  if (plannerStore.amplifySubscription) {
    Object.values(plannerStore.amplifySubscription).map(subscription => {
      subscription.unsubscribe();
    });

    plannerStore.setAmplifySubscription(null);
  }
}

const updateSessionLabel = session => {
  let label = `${session.stock} ${session.stock > 1 ? 'places restantes' : 'place restante'}`;
  if (session.activity_type === 'TERRAIN') {
    label = `${session.stock} ${session.stock > 1 ? 'terrains' : 'terrain'}`;
  }
  if (session.activity_type === 'ANNIVERSARY') {
    label = `${session.stock} ${session.stock > 1 ? 'anniversaires' : 'anniversaire'}`;
  }
  if (session.activity_type === 'ESCAPE') {
    label = `${session.stock} ${session.stock > 1 ? 'salles' : 'salle'}`;
  }
  if (session.activity_type === 'POSTE') {
    label = `${session.stock} ${session.stock > 1 ? 'postes' : 'poste'}`;
  }

  return label
}

const computeSession = ({ session, activities, site }) => {

  const { timezone, workspace, codeSiteComptage, isInternalSession } = site;

  const sessionActivities = []

  if (isInternalSession) {
    if (session.product_activity && session.product_activity.length) {
      session.product_activity.map(pa => {
        const activity = activities.find(activity => activity.uuid === pa.activity);
        if (activity && !sessionActivities.some(item => item.session.id === session.id)) {
          sessionActivities.push({ activity, session });
        }
      })
    }
  } else {
    const activity = activities.find(activity => {
      return activity.horanetActivity && parseInt(activity.horanetActivity.code) === parseInt(session.activity)
    });
    if (activity) {
      sessionActivities.push({ activity, session });
    }
  }

  const placesDisponibles = session.max_participant - ((session.participants || []).length + (session?.pre_reserved_participants?.items || []).length);

  const isDisabled = fromUnixTime(session.start_time) < new frenchDate() || !(placesDisponibles > 0);


  return sessionActivities.map(item => {
    const { activity } = item;

    let ableToReserve = true;

    if (activity && activity.reservationHoursLimit) {
      ableToReserve = now.add(activity.reservationHoursLimit, 'hours').isBefore(session.timestampDebutCreneau);
    }

    const sessionComputed =  {
      start_time: session.start_time * 1000,
      end_time: session.end_time * 1000,
      date: frenchFormatedDate(timezone, session.start_time * 1000, 'dd MMM (EEE)'),
      type: session.name,
      stock: placesDisponibles,
      justOnePlace: placesDisponibles === 1,
      startTime: frenchFormatedDate(timezone, session.start_time * 1000, "HH'h'mm"),
      endTime: frenchFormatedDate(timezone, session.end_time * 1000, "HH'h'mm"),
      startDate: frenchFormatedDate(timezone, session.start_time * 1000, 'dd/MM/yyyy'),
      endDate: frenchFormatedDate(timezone, session.end_time * 1000, 'dd/MM/yyyy'),
      codes: [session.id.toString()],
      id: session.id,
      isDisabled: isDisabled,
      hasStock: placesDisponibles && placesDisponibles > 0,
      area: activity.area ? activity.area.name : null,
      areas: activity.areas,
      activity_description: activity.description ? activity.description.replace(/'/g, "\\'") : '',
      activity_color: activity.color,
      activity_type: activity.type,
      group: activity.group,
      groupCode: activity.groupCode,
      activity_codes: [isInternalSession ? activity.uuid : activity.horanetActivity.code],
      product_reference: session.referenceProduit,
      redirect: !!activity.redirectToTda && !isDisabled && ableToReserve,
      showStock: !activity.hideStock && ableToReserve,
      grouped: false,
      hoursReserveLimitation: !!activity.reservationHoursLimit,
      reservationLimitMessage: activity.reservationLimitMessage
    }

    sessionComputed.stockLabel = updateSessionLabel(sessionComputed);

    return sessionComputed;
  })

}

const sessionCreateEdit = (session) => {
  const plannerStore = usePlannerStore();

  const plannerInfo = {...plannerStore.plannerInfo }

  let haveChanges = false;

  Object.keys(plannerInfo).forEach(key => {
    if (plannerInfo[key].isCustomPlanner) {
      return;
    }

    const computedSession = computeSession({ session: { ...session, participants: session.participants ? JSON.parse(session.participants) : [] }, activities: plannerInfo[key].pimActivities || [], site: plannerStore.siteInfo });

    plannerInfo[key].planner.columns = plannerInfo[key].planner.columns.map(column => {
      const index = column.items.findIndex(item => item.codes.includes(session.id));

      if (index !== -1) {
        const participants = session.participants ? JSON.parse(session.participants) : [];

        const placesDisponibles = session.max_participant - (participants.length + (session.pre_reserved_participants.items || []).length);

        if (placesDisponibles > 0) {
          haveChanges = true;

          column.items[index] = computedSession[0];
        }
        return column;
      }

      // verify if session belong to any column

      const isSessionPassed = fromUnixTime(session.start_time) < new frenchDate();

      if (!isSessionPassed) {
        haveChanges = true;

        switch(plannerInfo[key].timeframe){
          case 'weekly': {
            if (format(fromUnixTime(session.start_time), 'yyyy-MM-dd') === column.dateFormated) {
              column.items.push(...computedSession)
            }
            break;
          }
          case 'daily':
          case 'byMonths': {
            computedSession.map(s => {
              const label = computeSessionLabel(s);

              if (column.label === label) {
                column.items.push(s);
              }
            })
            break;
          }
        }
      }
      return column;
    })

  })

  if (haveChanges) {
    plannerStore.setPlannerInfo(plannerInfo);
  }
}

const handleAddParticipantToSession = (sessions) => {
  const plannerStore = usePlannerStore();

  const plannerInfo = {...plannerStore.plannerInfo }

  let haveChanges = false;

  sessions.map(session => {
    Object.keys(plannerInfo).forEach(key => {
      if (plannerInfo[key].isCustomPlanner) {
        return;
      }
      plannerInfo[key].planner.columns = plannerInfo[key].planner.columns.map(column => {
        const index = column.items.findIndex(item => item.codes.includes(session.id));

        if (index !== -1) {

          const placesDisponibles = session.max_participant - ((session.participants || []).length + (session?.pre_reserved_participants?.items || []).length);

          haveChanges = true;

          const sessionUpdated = {
            ...column.items[index],
            isDisabled: fromUnixTime(session.start_time) < new frenchDate() || !(placesDisponibles > 0),
            stock: placesDisponibles,
            justOnePlace: placesDisponibles === 1,
            hasStock: session.placesDisponibles && session.placesDisponibles > 0
          }

          sessionUpdated.stockLabel = updateSessionLabel(sessionUpdated);

          column.items[index] = sessionUpdated;
        }

        return column
      })
    })
  }) 

  if (haveChanges) {
    plannerStore.setPlannerInfo(plannerInfo);
  }
}

const handleDeleteParticipantFromSession = (sessions) => {
  const plannerStore = usePlannerStore();

  const plannerInfo = {...plannerStore.plannerInfo }

  let haveChanges = false

  sessions.map(session => {
    Object.keys(plannerInfo).forEach(key => {
      if (plannerInfo[key].isCustomPlanner) {
        return;
      }
      plannerInfo[key].planner.columns = plannerInfo[key].planner.columns.map(column => {
        const index = column.items.findIndex(item => item.codes.includes(session.id));

        if (index !== -1) {
          const placesDisponibles = session.max_participant - ((session.participants || []).length + (session?.pre_reserved_participants?.items || []).length);

          if (placesDisponibles > 0) {
            haveChanges = true;

            const sessionUpdated = {
              ...column.items[index],
              isDisabled: fromUnixTime(session.start_time) < new frenchDate(),
              stock: placesDisponibles,
              justOnePlace: placesDisponibles === 1,
              hasStock: true
            }

            sessionUpdated.stockLabel = updateSessionLabel(sessionUpdated);

            column.items[index] = sessionUpdated;
          }
          return column;
        }

        const isSessionPassed = fromUnixTime(session.start_time) < new frenchDate();

        if (!isSessionPassed) {
          haveChanges = true;

          const computedSession = computeSession({ session, activities: plannerInfo[key].pimActivities || [], site: plannerStore.siteInfo });

          switch(plannerInfo[key].timeframe){
            case 'weekly': {
              if (format(fromUnixTime(session.start_time), 'yyyy-MM-dd') === column.dateFormated) {
                column.items.push(...computedSession);
              }
              break;
            }
            case 'daily': 
            case 'byMonths': {
              computedSession.map(s => {
                const label = computeSessionLabel(s);

                if (column.label === label) {
                  column.items.push(s);
                }
              })
            }
          }
        }

        column.items.sort((a, b) => a.start_time - b.start_time);

        return column;
      })

    })
  })

  if (haveChanges) {
    plannerStore.setPlannerInfo(plannerInfo);
  }
}

const handleSessionCreateEdit = (sessions) => {
  const plannerStore = usePlannerStore();

  const plannerInfo = {...plannerStore.plannerInfo }

  let haveChanges = false;

  sessions.map(session => {
    Object.keys(plannerInfo).forEach(key => {
      if (plannerInfo[key].isCustomPlanner) {
        return;
      }
      plannerInfo[key].planner.columns = plannerInfo[key].planner.columns.map(column => {
        const index = column.items.findIndex(item => item.codes.includes(session.id));

        const computedSession = computeSession({ session, activities: plannerInfo[key].pimActivities || [], site: plannerStore.siteInfo });

        if (index !== -1) {
          haveChanges = true
          column.items[index] = {...column.items[index], ...computedSession[0]};
          return
        }

        const isSessionPassed = fromUnixTime(session.start_time) < new frenchDate();

        if (!isSessionPassed) {
          haveChanges = true;

          switch(plannerInfo[key].timeframe){
            case 'weekly': {
              if (format(startOfDay(fromUnixTime(session.start_time)), 'yyyy-MM-dd') === column.dateFormated) {
                column.items.push(...computedSession);
                column.items.sort((a, b) => a.start_time - b.start_time);
              }
              break;
            }
            case 'daily':
            case 'byMonths': {
              computedSession.map(s => {
                const label = computeSessionLabel(s);

                if (column.label === label) {
                  column.items.push(s);
                }
              })
              break;
            }
          }
        }

        return column
      })
    })
  })


  if (haveChanges) {
    plannerStore.setPlannerInfo(plannerInfo);
  }
}

const handleDeleteSession = (session) => {
  const plannerStore = usePlannerStore();
  
  const plannerInfo = {...plannerStore.plannerInfo }

  let haveChanges = false;

  Object.keys(plannerInfo).forEach(key => {
    if (plannerInfo[key].isCustomPlanner) {
      return;
    }
    const columnIndex = plannerInfo[key].planner.columns.findIndex(column => {
      return column.items.some(item => item.codes.includes(session.id));
    })

    if (columnIndex !== -1) {
      const sessionIndex =  plannerInfo[key].planner.columns[columnIndex].items.findIndex(item => item.codes.includes(session.id));

      if (sessionIndex !== -1) {
        haveChanges = true;

        plannerInfo[key].planner.columns[columnIndex].items.splice(sessionIndex, 1);
      }
    }
  })

  if (haveChanges) {
    plannerStore.setPlannerInfo(plannerInfo);
  }
}

const handleReplicaSessions = (item) => {
  const plannerStore = usePlannerStore();

  const plannerInfo = {...plannerStore.plannerInfo }

  let haveChanges = false;

  (item.delete || []).map(session => {
    Object.keys(plannerInfo).forEach(key => {
      if (plannerInfo[key].isCustomPlanner) {
        return;
      }
      const columnIndex = plannerInfo[key].planner.columns.findIndex(column => {
        return column.items.findIndex(item => item.codes.includes(session.id));
      })

      if (columnIndex !== -1) {
        const sessionIndex =  plannerInfo[key].planner.columns[columnIndex].items.findIndex(item => item.codes.includes(session.id));

        if (sessionIndex !== -1) {
          haveChanges = true;

          plannerInfo[key].planner.columns[columnIndex].items.splice(sessionIndex, 1);
        }
      }
    })
  });

  const createdEditedItems = [...(item.create || []), ...(item.edit || [])];

  createdEditedItems.map(session => {
    sessionCreateEdit(session)
  })

  if (haveChanges) {
    plannerStore.setPlannerInfo(plannerInfo);
  }
}

const handleUpdateSession = (session) => {
  sessionCreateEdit(session)
}

const handleSubscription = async () => {
  const plannerStore = usePlannerStore();
  const amplifySubscriptions = {};

  const client = generateClient();

  await unsubscribe();

  const filter = {};

  if (plannerStore.siteInfo.isInternalSession) {
    filter.workspace = { eq: plannerStore.siteInfo.workspace.toUpperCase() };
  }

  if (!plannerStore.siteInfo.isInternalSession && ['postgres'].includes(plannerStore.siteInfo.horanetSource)) {
    filter.codeSiteComptage = { eq: parseInt(plannerStore.siteInfo.codeSiteComptage) };
  }

  amplifySubscriptions.addParticipant = client.graphql({ query: subscriptions.onAddParticipantsToSession, authToken: 'test' }).subscribe({
    next: ({ data }) => {
      const response = data.onAddParticipantsToSession;
      if (response.success) {
        handleAddParticipantToSession(JSON.parse(response.data))
      }
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteParticipant = client.graphql({ query: subscriptions.onDeleteParticipantsFromSession, authToken: 'test' }).subscribe({
    next: ({ data }) => {
      const response = data.onDeleteParticipantsFromSession;
      if (response.success) {
        handleDeleteParticipantFromSession(JSON.parse(response.data))
      }
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.handleSessionCreateEdit = client.graphql({ query: subscriptions.onHandleSessionCreateEdit, authToken: 'test' }).subscribe({
    next: ({ data }) => {
      const response = data.onHandleSessionCreateEdit;

      if (response.success) {
        handleSessionCreateEdit(JSON.parse(response.data))
      }

    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteSession = client.graphql({ query: subscriptions.onDeleteSession, variables: { filter: filter } , authToken: 'test'}).subscribe({
    next: ({ data }) => {
      const response = data.onDeleteSession;
      handleDeleteSession(response)
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.handleReplicaSessions = client.graphql({ query: subscriptions.onHandleUpdateReplicaSessions, authToken: 'test' }).subscribe({
    next: ({ data }) => {
      const response = data.onHandleUpdateReplicaSessions;
      handleReplicaSessions(response)
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.updateSession = client.graphql({ query: subscriptions.onUpdateSession, variables: { filter: filter }, authToken: 'test' }).subscribe({
    next: ({ data }) => {
    console.log('data :', data);
      const response = data.onUpdateSession;
      handleUpdateSession(response)
    },
    error: error => {
    console.log('error :', error);
      console.warn(error);
    }
  });

  plannerStore.setAmplifySubscription(amplifySubscriptions);
}

export { handleSubscription };